/**
 * Site Phase Modals
 * Collection of modals for phase management:
 * - CompletePhaseModal
 * - SkipPhaseModal
 * - AddPhaseModal
 * - UpdateProgressModal
 */

import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';

// ========================================
// Complete Phase Modal
// ========================================
export const CompletePhaseModal = ({
  isOpen,
  onClose,
  phase,
  onConfirm,
  loading = false
}) => {
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (isOpen) {
      setNotes('');
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm && onConfirm({ notes });
  };

  if (!phase) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Complete Phase">
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-green-900">{phase.name}</p>
                <p className="text-sm text-green-700">Mark this phase as completed</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Completion Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Add any notes about the completed phase..."
            />
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-3 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Current Progress:</span>
              <span className="font-medium">{Math.round(phase.progress)}%</span>
            </div>
            {phase.tasks && phase.tasks.length > 0 && (
              <div className="flex justify-between text-gray-600 mt-1">
                <span>Tasks Completed:</span>
                <span className="font-medium">
                  {phase.tasks.filter(t => t.status === 'completed').length}/{phase.tasks.length}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? 'Completing...' : 'Complete Phase'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ========================================
// Skip Phase Modal
// ========================================
export const SkipPhaseModal = ({
  isOpen,
  onClose,
  phase,
  onConfirm,
  loading = false
}) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      setReason('');
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!reason.trim()) return;
    onConfirm && onConfirm({ reason });
  };

  if (!phase) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Skip Phase">
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-yellow-900">{phase.name}</p>
                <p className="text-sm text-yellow-700">Skip this phase (will not be counted in progress)</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason for Skipping <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              placeholder="Please explain why this phase is being skipped..."
            />
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
            <p className="font-medium">Note:</p>
            <p>Skipped phases will not contribute to the overall site progress calculation.</p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !reason.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 disabled:opacity-50"
          >
            {loading ? 'Skipping...' : 'Skip Phase'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ========================================
// Add Phase Modal
// ========================================
export const AddPhaseModal = ({
  isOpen,
  onClose,
  existingPhases = [],
  templates = [],
  onConfirm,
  loading = false
}) => {
  const [mode, setMode] = useState('template'); // 'template' or 'custom'
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [customName, setCustomName] = useState('');
  const [weight, setWeight] = useState(10);
  const [sequence, setSequence] = useState(existingPhases.length + 1);

  useEffect(() => {
    if (isOpen) {
      setMode('template');
      setSelectedTemplate('');
      setCustomName('');
      setWeight(10);
      setSequence(existingPhases.length + 1);
    }
  }, [isOpen, existingPhases.length]);

  // Filter out phases that already exist
  const existingKeys = existingPhases.map(p => p.phaseKey);
  const availableTemplates = templates.filter(t => !existingKeys.includes(t.key));

  const handleSubmit = (e) => {
    e.preventDefault();

    if (mode === 'template' && !selectedTemplate) return;
    if (mode === 'custom' && !customName.trim()) return;

    const selectedTemplateData = templates.find(t => t.key === selectedTemplate);

    onConfirm && onConfirm({
      phaseKey: mode === 'template' ? selectedTemplate : `custom_${Date.now()}`,
      name: mode === 'template' ? selectedTemplateData?.name : customName.trim(),
      weight: Number(weight),
      sequence: Number(sequence)
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Phase">
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* Mode Selection */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode('template')}
              className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg border-2 transition-colors ${
                mode === 'template'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`}
            >
              From Template
            </button>
            <button
              type="button"
              onClick={() => setMode('custom')}
              className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg border-2 transition-colors ${
                mode === 'custom'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`}
            >
              Custom Phase
            </button>
          </div>

          {/* Template Selection */}
          {mode === 'template' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Phase Template
              </label>
              {availableTemplates.length > 0 ? (
                <select
                  value={selectedTemplate}
                  onChange={(e) => {
                    setSelectedTemplate(e.target.value);
                    const tmpl = templates.find(t => t.key === e.target.value);
                    if (tmpl) setWeight(tmpl.weight);
                  }}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a phase...</option>
                  {availableTemplates.map(template => (
                    <option key={template.key} value={template.key}>
                      {template.name} ({template.weight}%)
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  All template phases have already been added.
                </p>
              )}
            </div>
          )}

          {/* Custom Phase Name */}
          {mode === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phase Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter phase name..."
              />
            </div>
          )}

          {/* Weight */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Weight (%) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              min={1}
              max={100}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Determines how much this phase contributes to overall progress
            </p>
          </div>

          {/* Sequence */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Position
            </label>
            <select
              value={sequence}
              onChange={(e) => setSequence(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {[...Array(existingPhases.length + 1)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}{i === 0 ? ' (First)' : i === existingPhases.length ? ' (Last)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || (mode === 'template' && !selectedTemplate) || (mode === 'custom' && !customName.trim())}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Adding...' : 'Add Phase'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ========================================
// Update Progress Modal
// ========================================
export const UpdateProgressModal = ({
  isOpen,
  onClose,
  phase,
  onConfirm,
  loading = false
}) => {
  const [progress, setProgress] = useState(0);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (isOpen && phase) {
      setProgress(Math.round(phase.progress || 0));
      setNotes('');
    }
  }, [isOpen, phase]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm && onConfirm({ progress: Number(progress), notes });
  };

  if (!phase) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Update Progress">
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="font-semibold text-blue-900">{phase.name}</p>
            <p className="text-sm text-blue-700">Current progress: {Math.round(phase.progress)}%</p>
          </div>

          {/* Progress Slider */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Progress: <span className="font-bold text-blue-600">{progress}%</span>
            </label>
            <input
              type="range"
              value={progress}
              onChange={(e) => setProgress(e.target.value)}
              min={0}
              max={100}
              step={5}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Progress Bar Preview */}
          <div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Quick Select Buttons */}
          <div className="flex gap-2 flex-wrap">
            {[0, 25, 50, 75, 100].map(value => (
              <button
                key={value}
                type="button"
                onClick={() => setProgress(value)}
                className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                  progress === value
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                }`}
              >
                {value}%
              </button>
            ))}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Update Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add notes about this progress update..."
            />
          </div>

          {/* Warning if setting to 100% */}
          {progress === 100 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
              <p className="font-medium">Note:</p>
              <p>Setting progress to 100% will complete all tasks in this phase.</p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Updating...' : 'Update Progress'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ========================================
// Change Site Status Modal
// ========================================
export const ChangeSiteStatusModal = ({
  isOpen,
  onClose,
  currentStatus,
  targetStatus,
  onConfirm,
  loading = false
}) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      setReason('');
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm && onConfirm({ status: targetStatus, reason });
  };

  const getStatusConfig = (status) => {
    const configs = {
      active: { label: 'Active', color: 'blue', icon: 'play' },
      on_hold: { label: 'On Hold', color: 'gray', icon: 'pause' },
      completed: { label: 'Completed', color: 'green', icon: 'check' }
    };
    return configs[status] || configs.active;
  };

  const targetConfig = getStatusConfig(targetStatus);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Site Status">
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div className={`bg-${targetConfig.color}-50 border border-${targetConfig.color}-200 rounded-lg p-4`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 bg-${targetConfig.color}-100 rounded-full flex items-center justify-center`}>
                {targetConfig.icon === 'play' && (
                  <svg className={`w-6 h-6 text-${targetConfig.color}-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  </svg>
                )}
                {targetConfig.icon === 'pause' && (
                  <svg className={`w-6 h-6 text-${targetConfig.color}-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                {targetConfig.icon === 'check' && (
                  <svg className={`w-6 h-6 text-${targetConfig.color}-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              <div>
                <p className={`font-semibold text-${targetConfig.color}-900`}>
                  Change to {targetConfig.label}
                </p>
                <p className={`text-sm text-${targetConfig.color}-700`}>
                  {targetStatus === 'on_hold' && 'Pause all site activities'}
                  {targetStatus === 'active' && 'Resume site activities'}
                  {targetStatus === 'completed' && 'Mark site as fully completed'}
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason {targetStatus === 'on_hold' ? <span className="text-red-500">*</span> : '(Optional)'}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              required={targetStatus === 'on_hold'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={
                targetStatus === 'on_hold'
                  ? 'Please explain why the site is being put on hold...'
                  : 'Add any notes about this status change...'
              }
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || (targetStatus === 'on_hold' && !reason.trim())}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 ${
              targetStatus === 'completed' ? 'bg-green-600 hover:bg-green-700' :
              targetStatus === 'on_hold' ? 'bg-gray-600 hover:bg-gray-700' :
              'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? 'Updating...' : 'Confirm Change'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ========================================
// Edit Phase Modal
// ========================================
export const EditPhaseModal = ({
  isOpen,
  onClose,
  phase,
  onConfirm,
  onDelete,
  loading = false
}) => {
  const [name, setName] = useState('');
  const [weight, setWeight] = useState(10);
  const [notes, setNotes] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (isOpen && phase) {
      setName(phase.name || '');
      setWeight(phase.weight || 10);
      setNotes(phase.notes || '');
      setShowDeleteConfirm(false);
    }
  }, [isOpen, phase]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onConfirm && onConfirm({
      name: name.trim(),
      weight: Number(weight),
      notes: notes.trim()
    });
  };

  const handleDelete = () => {
    onDelete && onDelete(phase.id);
  };

  if (!phase) return null;

  // Check if phase can be edited/deleted
  const canModify = phase.status === 'pending' || phase.status === 'in_progress';
  const canDelete = phase.status === 'pending';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Phase">
      {showDeleteConfirm ? (
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-red-900">Delete "{phase.name}"?</p>
                <p className="text-sm text-red-700">This action cannot be undone. All tasks in this phase will also be deleted.</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {loading ? 'Deleting...' : 'Delete Phase'}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Phase Status Info */}
            <div className={`text-xs px-3 py-2 rounded-lg ${
              phase.status === 'completed' ? 'bg-green-50 text-green-700' :
              phase.status === 'in_progress' ? 'bg-blue-50 text-blue-700' :
              phase.status === 'skipped' ? 'bg-yellow-50 text-yellow-700' :
              'bg-gray-50 text-gray-700'
            }`}>
              Status: <span className="font-medium capitalize">{phase.status?.replace('_', ' ')}</span>
              {!canModify && (
                <span className="ml-2">(Cannot edit completed/skipped phases)</span>
              )}
            </div>

            {/* Phase Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phase Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!canModify}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Enter phase name..."
              />
            </div>

            {/* Weight */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Weight (%) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                disabled={!canModify}
                min={1}
                max={100}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                Determines how much this phase contributes to overall progress
              </p>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={!canModify}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Add any notes about this phase..."
              />
            </div>

            {/* Phase Key Info */}
            <div className="text-xs text-gray-500">
              <span className="font-medium">Phase Key:</span> {phase.phaseKey}
              <span className="mx-2">|</span>
              <span className="font-medium">Sequence:</span> {phase.sequence}
            </div>
          </div>

          <div className="mt-6 flex justify-between">
            {/* Delete Button */}
            <div>
              {canDelete && onDelete && (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                >
                  Delete Phase
                </button>
              )}
            </div>

            {/* Save/Cancel Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              {canModify && (
                <button
                  type="submit"
                  disabled={loading || !name.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              )}
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default {
  CompletePhaseModal,
  SkipPhaseModal,
  AddPhaseModal,
  UpdateProgressModal,
  ChangeSiteStatusModal,
  EditPhaseModal
};
